import { useContext, useMemo } from 'react';
import { DeleteDialog, GlobalMenu } from '@components/index';

import { useMutation, useQueryClient } from 'react-query';
import { IOnboardingFile } from '@models/Onboarding.model';
import { useState } from '@hookstate/core';
import { patchFileStatus } from '@services/queries/onboarding/patchFileStatus';
import { NavigationContext } from '@layout/navigation/navigation';
import { deleteImporter } from '@services/queries/onboarding/runImporter';
import {
  closeFile,
  undoImport as mixpanelUndoImport,
} from '@services/mixpanel/trackers/Onboarding';

interface IFileQuickActionMenu {
  file: IOnboardingFile;
}

export const FileQuickActionMenu = ({ file }: IFileQuickActionMenu) => {
  const queryClient = useQueryClient();

  const { studioId, studioName, isSuperAdmin } = useContext(NavigationContext);

  const openDelete = useState(false);
  const openCannotDelete = useState(false);
  const openUndoImport = useState(false);

  const fileStatusMutation = useMutation(patchFileStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(['onboarding', 'files']);
      openDelete.set(false);
      closeFile(studioName, file.id);
    },
  });

  const deleteFileImport = useMutation(deleteImporter, {
    onSuccess: () => {
      queryClient.invalidateQueries(['onboarding', 'files']);
      openUndoImport.set(false);
      mixpanelUndoImport(studioName, file.id);
    },
  });

  const itemsList = useMemo(() => {
    const undoImport = {
      iconName: 'trash',
      label: 'Delete Import',
      onClick: () => {
        openUndoImport.set(true);
      },
    };

    if (file.status === 'open' && isSuperAdmin && !file.isManual)
      return [undoImport];
    return [];
  }, []);

  return itemsList.length ? (
    <>
      <GlobalMenu title="Quick Actions" align="end" itemList={itemsList} />
      {openDelete.get() && (
        <DeleteDialog
          onDelete={() => {
            fileStatusMutation.mutate({
              fileId: file.fileId,
              status: 'closed',
              studioId,
            });
          }}
          open={openDelete}
          buttonText="Close Anyway"
          text="There are still unverified products in this list."
          hideCancel
        />
      )}

      {openUndoImport.get() && (
        <DeleteDialog
          title="Delete Import"
          onDelete={() => {
            deleteFileImport.mutate({ fileId: file.fileId, studioId });
          }}
          onDeleteLoading={deleteFileImport.isLoading}
          open={openUndoImport}
          buttonText="Delete"
          text={
            <span>
              This file is related to {file.totalItems} assets. This action
              causes <span className="bold">loss of data</span> (including any
              edits made in Beam) and{' '}
              <span className="bold">cannot be undone</span>.
            </span>
          }
          hideCancel
          inputCheck
        />
      )}

      {openCannotDelete.get() && (
        <DeleteDialog
          onDelete={() => {
            openCannotDelete.set(false);
          }}
          open={openCannotDelete}
          buttonText="Close"
          title="Identification Requests Found"
          text="You cannot close if there are open identification requests"
          hideCancel
        />
      )}
    </>
  ) : null;
};
